import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaEnvelope,
  FaFacebookF,
  FaGlobe,
  FaInstagram,
  FaLinkedinIn,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaYoutube,
} from "react-icons/fa";
import { FiArrowRight, FiExternalLink } from "react-icons/fi";

import { getSettings } from "./../../services/settingsServices";

const safeStr = (value) => (typeof value === "string" ? value.trim() : "");

const normalizeExternalUrl = (value = "") => {
  const url = safeStr(value);

  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;

  return `https://${url}`;
};

const normalizePhoneHref = (value = "") => {
  const phone = safeStr(value).replace(/\s+/g, "").replace(/[().-]/g, "");
  return phone ? `tel:${phone}` : "";
};

function FooterLink({ to, children }) {
  return (
    <Link
      to={to}
      className="group inline-flex items-center gap-2 text-sm font-semibold text-slate-300 transition hover:text-white"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-slate-600 transition group-hover:bg-sky-400" />
      {children}
    </Link>
  );
}

function SocialButton({ href, label, icon: Icon }) {
  if (!href) return null;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      className="group relative inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-white/5 text-white/80 shadow-lg shadow-black/10 transition duration-300 hover:-translate-y-0.5 hover:bg-white/10 hover:text-white"
    >
      <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition duration-700 group-hover:translate-x-full" />
      <Icon className="relative h-4 w-4" />
    </a>
  );
}

function ContactLine({ icon: Icon, children }) {
  if (!children) return null;

  return (
    <div className="flex items-start gap-3 text-sm font-semibold leading-6 text-slate-300">
      <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white/8 text-sky-300">
        <Icon className="h-4 w-4" />
      </span>

      <div className="min-w-0 pt-1">{children}</div>
    </div>
  );
}

export default function Footer() {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        const currentSettings = Array.isArray(data) ? data[0] : data;

        if (mounted) {
          setSettings(currentSettings || null);
        }
      } catch (error) {
        console.error("Erreur chargement settings footer:", error);
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const computed = useMemo(() => {
    const nomMairie =
      safeStr(settings?.nom_mairie) || safeStr(settings?.ville) || "Votre commune";

    const adresse =
      safeStr(settings?.adresse) ||
      safeStr(settings?.adresse_mairie) ||
      safeStr(settings?.address);

    const codePostal = safeStr(settings?.code_postal);
    const ville = safeStr(settings?.ville);

    const telephone = safeStr(settings?.telephone_contact);
    const email = safeStr(settings?.email_contact);
    const siteWeb = normalizeExternalUrl(settings?.site_web);

    const logo = safeStr(settings?.logo_mairie);
    const primaryColor = safeStr(settings?.primary_color) || "#1277B9";

    return {
      nomMairie,
      adresse,
      codePostal,
      ville,
      telephone,
      phoneHref: normalizePhoneHref(telephone),
      email,
      siteWeb,
      logo,
      primaryColor,

      facebook: normalizeExternalUrl(settings?.link_facebook),
      instagram: normalizeExternalUrl(settings?.link_instagram),
      youtube: normalizeExternalUrl(settings?.link_youtube),
      linkedin: normalizeExternalUrl(settings?.link_linkedin),
    };
  }, [settings]);

  const year = new Date().getFullYear();

  return (
    <footer className="relative mt-16 overflow-hidden bg-slate-950 text-white">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-28 top-12 h-80 w-80 rounded-full bg-sky-500/10 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-emerald-400/10 blur-3xl" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </div>

      <div className="relative mx-auto max-w-[1380px] px-4 py-12 sm:px-6 lg:px-8 lg:py-14">
        <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr_0.9fr]">
          <section className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 shadow-2xl shadow-black/10 backdrop-blur">
            <div className="flex items-center gap-4">
              {computed.logo ? (
                <img
                  src={computed.logo}
                  alt={computed.nomMairie}
                  className="h-16 w-16 shrink-0 object-contain"
                  loading="lazy"
                />
              ) : (
                <div
                  className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-xl font-bold text-white shadow-lg shadow-black/20"
                  style={{ backgroundColor: computed.primaryColor }}
                >
                  {computed.nomMairie.charAt(0)}
                </div>
              )}

              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-slate-400">
                  Site officiel
                </p>
                <h2 className="mt-1 text-2xl font-bold leading-tight text-white md:text-3xl">
                  {computed.nomMairie}
                </h2>
              </div>
            </div>

            <address className="mt-6 space-y-3 not-italic">
              {computed.adresse && (
                <ContactLine icon={FaMapMarkerAlt}>
                  <span>
                    {computed.adresse}
                    {(computed.codePostal || computed.ville) && (
                      <>
                        <br />
                        {[computed.codePostal, computed.ville]
                          .filter(Boolean)
                          .join(" ")}
                      </>
                    )}
                  </span>
                </ContactLine>
              )}

              {!computed.adresse && (computed.codePostal || computed.ville) && (
                <ContactLine icon={FaMapMarkerAlt}>
                  {[computed.codePostal, computed.ville].filter(Boolean).join(" ")}
                </ContactLine>
              )}

              {computed.telephone && (
                <ContactLine icon={FaPhoneAlt}>
                  <a
                    href={computed.phoneHref}
                    className="transition hover:text-sky-300 hover:underline"
                  >
                    {computed.telephone}
                  </a>
                </ContactLine>
              )}

              {computed.email && (
                <ContactLine icon={FaEnvelope}>
                  <a
                    href={`mailto:${computed.email}`}
                    className="break-all text-sky-300 transition hover:text-sky-200 hover:underline"
                  >
                    {computed.email}
                  </a>
                </ContactLine>
              )}

              {computed.siteWeb && (
                <ContactLine icon={FaGlobe}>
                  <a
                    href={computed.siteWeb}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 transition hover:text-sky-300 hover:underline"
                  >
                    Site officiel
                    <FiExternalLink className="h-3.5 w-3.5" />
                  </a>
                </ContactLine>
              )}
            </address>
          </section>

          <section className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 shadow-2xl shadow-black/10 backdrop-blur">
            <h3 className="text-lg font-bold text-white">Liens utiles</h3>

            <div className="mt-5 grid gap-3">
              <FooterLink to="/actualites">Actualités</FooterLink>
              <FooterLink to="/agenda">Agenda</FooterLink>
              <FooterLink to="/services-municipaux">Services municipaux</FooterLink>
              <FooterLink to="/carte-interets">Carte des lieux d’intérêt</FooterLink>
              <FooterLink to="/publications">Publications</FooterLink>
              <FooterLink to="/grands-projets">Grands projets</FooterLink>
              <FooterLink to="/mentions-legales">Mentions légales</FooterLink>
            </div>
          </section>

          <section className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 shadow-2xl shadow-black/10 backdrop-blur">
            <h3 className="text-lg font-bold text-white">Restez connectés</h3>

            <p className="mt-3 text-sm font-medium leading-7 text-slate-300">
              Suivez l’actualité de la commune et retrouvez les informations
              importantes publiées par la mairie.
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <SocialButton
                href={computed.facebook}
                label="Facebook"
                icon={FaFacebookF}
              />
              <SocialButton
                href={computed.instagram}
                label="Instagram"
                icon={FaInstagram}
              />
              <SocialButton
                href={computed.linkedin}
                label="LinkedIn"
                icon={FaLinkedinIn}
              />
              <SocialButton
                href={computed.youtube}
                label="YouTube"
                icon={FaYoutube}
              />

              {computed.email &&
                !computed.facebook &&
                !computed.instagram &&
                !computed.linkedin &&
                !computed.youtube && (
                  <SocialButton
                    href={`mailto:${computed.email}`}
                    label="Contact email"
                    icon={FaEnvelope}
                  />
                )}
            </div>

            <Link
              to="/contact"
              className="group mt-6 inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-white px-4 text-sm font-bold text-slate-950 shadow-sm transition hover:-translate-y-0.5 hover:bg-slate-100"
            >
              Contacter la mairie
              <FiArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
            </Link>
          </section>
        </div>

        <div className="mt-8 flex flex-col gap-5 border-t border-white/10 pt-6 text-sm text-slate-400 md:flex-row md:items-center md:justify-between">
          <div className="text-center md:text-left">
            © {year} {computed.nomMairie}. Tous droits réservés.
            <span className="mx-2 hidden text-slate-600 md:inline">•</span>
            <span className="block md:inline">
              Site propulsé par{" "}
              <span className="font-bold text-slate-200">MonAppliMairie</span>
            </span>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link
              to="/politique-confidentialite"
              className="transition hover:text-white hover:underline"
            >
              Confidentialité
            </Link>

            <Link
              to="/mentions-legales"
              className="transition hover:text-white hover:underline"
            >
              Mentions légales
            </Link>

            <Link
              to="/conditions-utilisation"
              className="transition hover:text-white hover:underline"
            >
              Conditions d’utilisation
            </Link>

            <Link
              to="/plan-du-site"
              className="transition hover:text-white hover:underline"
            >
              Plan du site
            </Link>
          </div>
        </div>

        <div className="mt-4 text-center text-xs font-medium text-slate-500 md:text-right">
          Conception et développement : Laurent VICTORIN
        </div>
      </div>
    </footer>
  );
}